import { React, useState, useEffect } from 'react';
import axios from 'axios';
import '../../App.css';
import { Grid, TextField, Box, Button, Paper, Stack, FormControlLabel, Checkbox } from '@mui/material';
import Notification from '../Notification';
import { useNavigate } from 'react-router-dom';

const initialValues = {
  config_key: '',
  config_value: '',
  status: true,
};

const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
  </span>
);

function AddGratuityConfigurationForm(props) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const navigate = useNavigate();
  const pushNotify = (notification) => {
    if (props.onNotify) {
      props.onNotify(notification);
    } else {
      setNotify(notification);
    }
  };

  useEffect(() => {
    async function getGratuityConfigById() {
      if (!props.fetch_id) {
        setValues(initialValues);
        setErrors({});
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/configure/gratuity-configure/get/${props.fetch_id}`
        );
        const raw = response.data?.data ?? response.data ?? {};
        const config = Array.isArray(raw) ? raw[0] || {} : raw;
        setValues({
          config_key: config.config_key || '',
          config_value:
            config.config_value !== null && config.config_value !== undefined ? String(config.config_value) : '',
          status: Number(config.status ?? 1) === 1,
        });
      } catch (e) {
        handleApiError(e, 'Failed to load gratuity config');
      }
    }

    getGratuityConfigById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.fetch_id]);

  const validate = () => {
    const temp = {};
    temp.config_key = values.config_key ? '' : 'This field is required.';
    temp.config_value = values.config_value ? '' : 'This field is required.';
    setErrors(temp);
    return Object.values(temp).every((x) => x === '');
  };

  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const convertToDefEventPara = (name, value) => ({
    target: {
      name,
      value,
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      config_key: values.config_key.trim(),
      config_value: String(values.config_value).trim(),
      status: values.status ? 1 : 0,
    };

    const request = props.fetch_id
      ? axios.put(`${process.env.REACT_APP_API_BASE_URL}/configure/gratuity-configure/edit/${props.fetch_id}`, payload)
      : axios.post(`${process.env.REACT_APP_API_BASE_URL}/configure/gratuity-configure/add`, payload);

    request
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          if (props.onRefresh) props.onRefresh();
          if (!props.fetch_id) resetForm();
          pushNotify({
            isOpen: true,
            message: props.fetch_id ? 'Gratuity configuration updated successfully' : 'Gratuity configuration added successfully',
            type: 'success',
          });
          if (props.setOpenPopup) props.setOpenPopup(false);
          if (props.onCloseModal) props.onCloseModal();
        }
      })
      .catch((e) => {
        handleApiError(e, 'Failed to save gratuity config');
      });
  };

  const handleApiError = (e, fallbackMessage) => {
    if (e.response && e.response.status === 401) {
      pushNotify({
        isOpen: true,
        message: 'Session Expired!',
        type: 'error',
      });
      setTimeout(() => {
        navigate('/');
      }, 1000);
      return;
    }

    const errMsg = e.response?.data?.error_message || e.response?.data?.message || fallbackMessage;
    pushNotify({
      isOpen: true,
      message: errMsg,
      type: 'error',
    });
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', margin: '-10px', padding: '30px', fontWeight: '500px' }}>
      <Paper variant="outlined" elevation={5} sx={{ width: '600px', marginTop: '-20px' }}>
        <form className="classes.root" autoComplete="off" onSubmit={handleSubmit}>
          <Stack spacing={2} sx={{ margin: '30px' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  name="config_key"
                  label={<RequiredLabel label="Config Key" />}
                  size="small"
                  fullWidth
                  value={values.config_key}
                  onChange={handleInputChange}
                  {...(errors.config_key && { error: true, helperText: errors.config_key })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  name="config_value"
                  label={<RequiredLabel label="Config Value" />}
                  size="small"
                  fullWidth
                  value={values.config_value}
                  onChange={handleInputChange}
                  {...(errors.config_value && { error: true, helperText: errors.config_value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={values.status}
                      onChange={(e) => handleInputChange(convertToDefEventPara('status', e.target.checked))}
                    />
                  }
                  label="Active?"
                />
              </Grid>
            </Grid>

            <Grid container className="space-around" sx={{ mt: 2 }} style={{ justifyContent: 'center', marginTop: '20px' }}>
              <Grid item>
                <Button variant="contained" color="error" onClick={resetForm}>
                  Reset
                </Button>
              </Grid>
              <Grid item>
                <Button style={{ marginLeft: '10px' }} variant="contained" type="submit" color="success">
                  {props.fetch_id ? 'Update' : 'Submit'}
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </form>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}

export default AddGratuityConfigurationForm;
